import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getSelfCheckInOverview,
  startSelfCheckIn,
  submitPulseSurveyResponse,
} from "@/features/pulse-surveys/pulse-surveys.api";
import type {
  SubmitResponse,
  SubmitResponseResult,
} from "@/features/pulse-surveys/schemas/pulse-survey.schema";
import type {
  SelfCheckInOverview,
  StartSelfCheckInRequest,
  StartSelfCheckInResponse,
} from "@/features/pulse-surveys/schemas/self-check-in.schema";

/// Nested under ["pulse-surveys", wsId, "me"] ON PURPOSE: the submit-response
/// mutation invalidates that prefix, which flips availability to done_today
/// when the last required answer lands.
function key(workspaceId: string | undefined): ReadonlyArray<unknown> {
  return ["pulse-surveys", workspaceId, "me", "self-check-in"] as const;
}

export function useSelfCheckInOverview(workspaceId: string | undefined) {
  return useQuery<SelfCheckInOverview>({
    queryKey: key(workspaceId),
    queryFn: () => {
      if (!workspaceId) throw new Error("workspaceId required");
      return getSelfCheckInOverview(workspaceId);
    },
    enabled: Boolean(workspaceId),
    staleTime: 30_000,
  });
}

export function useStartSelfCheckIn(workspaceId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<StartSelfCheckInResponse, Error, StartSelfCheckInRequest>({
    mutationFn: (body) => {
      if (!workspaceId) throw new Error("workspaceId required");
      return startSelfCheckIn(workspaceId, body);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["pulse-surveys", workspaceId, "me"] });
    },
  });
}

export function useSubmitSelfCheckInResponse(
  workspaceId: string | undefined,
  deliveryId: string | null,
) {
  const qc = useQueryClient();
  return useMutation<SubmitResponseResult, Error, SubmitResponse>({
    mutationFn: (body) => {
      if (!workspaceId) throw new Error("workspaceId required");
      if (!deliveryId) throw new Error("deliveryId required");
      return submitPulseSurveyResponse(workspaceId, deliveryId, body);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["pulse-surveys", workspaceId, "me"] });
    },
  });
}
